import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import './Experience.scss';
import NewlyQualifiedPT from 'url:../../../../assets/images/onboarding-trainer/newly-qualified-pt.webp';
import GrowingPersonalTrainer from 'url:../../../../assets/images/onboarding-trainer/growing-personal-trainer.webp';
import EstablishedCoach from 'url:../../../../assets/images/onboarding-trainer/established-coach.webp';

import { useGetOnboardingMasterData } from 'src/api/mutations/useGetOnboardingMasterData';
import { useSaveOnboardingStep } from 'src/api/mutations/useSaveOnboardingStep';
import { sessionStore } from 'src/store/sessionStore';
import { getUserIdFromToken } from 'src/utils/authUtils';
import { useGetOnboardingState } from 'src/api/mutations/useGetOnboardingState';

const IMAGE_MAP: Record<string, string> = {
  'newly-qualified': NewlyQualifiedPT,
  'growing-trainer': GrowingPersonalTrainer,
  'established-coach': EstablishedCoach,
};

interface ExperienceProps {
  onNext?: (id: string) => void;
  onSkip?: () => void;
}

export const Experience: React.FC<ExperienceProps> = ({ onNext, onSkip }) => {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSkipping, setIsSkipping] = useState(false);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  const { data: masterData } = useGetOnboardingMasterData();
  const { mutate: saveStep, mutateAsync: saveStepAsync, isPending } = useSaveOnboardingStep();

  const token = sessionStore((s) => s.token);
  const userId = getUserIdFromToken(token);

  const handleSkip = async () => {
    if (isPending || isSkipping) return;
    setIsSkipping(true);

    try {
      if (userId) {
        await saveStepAsync({ uid: userId, step: 'skip', data: { onboardingSkipped: true } });
      }
    } catch (err) {
      console.error('Failed to save skip state:', err);
    } finally {
      setIsSkipping(false);
      if (onSkip) {
        onSkip();
      } else {
        navigate('/onboarding-trainer/dashboard');
      }
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/onboarding-trainer/dashboard');
    }
  };

  const experienceData = masterData?.['onboarding_experience'];
  const options = experienceData?.options || [];

  // Mobile specific title based on the screenshot provided by the user
  const isMobile = window.innerWidth <= 768;
  const titleText = isMobile
    ? 'Which best describes your coaching style?'
    : 'Which stage best reflects your personal training experience?';

  const { data: stateData } = useGetOnboardingState(userId || undefined);

  useEffect(() => {
    if (stateData?.onboarding?.step1_experience && !selectedId) {
      const savedExp = stateData.onboarding.step1_experience;
      setSelectedId(Array.isArray(savedExp) ? savedExp[0] : savedExp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateData?.onboarding?.step1_experience]);

  useEffect(() => {
    if (cardsRef.current.length > 0) {
      gsap.fromTo(
        cardsRef.current,
        { opacity: 0, y: 25 },
        {
          opacity: 1,
          y: 0,
          duration: 0.85,
          stagger: 0.15,
          ease: 'power3.out',
        },
      );
    }
  }, [options.length]);

  return (
    <div className="experience-container">
      <div className="experience-header">
        <h1 className="title">{experienceData?.title || titleText}</h1>
        <p className="subtitle">
          {experienceData?.subtitle ||
            'Choose the option that feels closest to where you are right now.'}
        </p>
      </div>

      <div className="experience-grid">
        {options.map((option, index) => (
          <div
            key={option.slug}
            ref={(el) => {
              cardsRef.current[index] = el;
            }}
            className={`experience-card ${selectedId === option.slug ? 'selected' : ''}`}
            onClick={() => {
              if (isPending) return;
              setSelectedId(option.slug);
              if (userId) {
                saveStep({ uid: userId, step: 1, data: [option.slug] });
              }
              if (onNext) setTimeout(() => onNext(option.slug), 400);
            }}
            style={{ opacity: 0 }}
          >
            <div className="hexagon-border">
              <img src={IMAGE_MAP[option.slug] || NewlyQualifiedPT} alt={option.title} />
            </div>
            <div className="radio-circle">
              {selectedId === option.slug && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <div className="card-content">
              <h3>{option.title}</h3>
              <p>{option.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="experience-footer">
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
        </div>

        <button
          type="button"
          className="btn-skip-onboarding"
          onClick={handleSkip}
          disabled={isPending || isSkipping}
          aria-label="Skip onboarding"
        >
          {isSkipping ? 'Skipping...' : 'Skip'}
        </button>

        <div className="back-btn-wrapper">
          <button className="btn-back" onClick={handleBack}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {'Back'}
          </button>
        </div>
      </div>
    </div>
  );
};
